import React from 'react';
import { Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility for merging tailwind classes properly
 */
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const variants = {
  primary:   'bg-[#0176D3] text-white hover:bg-[#014486] shadow-sm border-[#0176D3]',
  secondary: 'bg-[#F3F2F2] text-[#080707] hover:bg-[#E5E5E5] border-[#D8DDE6]',
  danger:    'bg-[#BA0517] text-white hover:bg-[#8E030F] shadow-sm border-[#BA0517]',
  success:   'bg-[#04844B] text-white hover:bg-[#036B3A] shadow-sm border-[#04844B]',
  warning:   'bg-[#FFB75D] text-[#080707] hover:bg-[#FFAA40] shadow-sm border-[#FFB75D]',
  outline:   'bg-white border-[#D8DDE6] text-[#0176D3] hover:bg-[#F3F2F2] hover:text-[#005FB2] hover:border-[#D8DDE6]',
  ghost:     'bg-transparent text-[#706E6B] hover:bg-[#F3F2F2] hover:text-[#080707] border-transparent',
};

const sizes = {
  sm: 'h-8 px-3.5 text-[12px]',
  md: 'h-9 px-4 text-[13px]',
  lg: 'h-10 px-6 text-[14px]',
  xl: 'h-12 px-8 text-[15px]',
};

const Button = React.forwardRef(({ 
  className, 
  variant = 'primary', 
  size = 'md', 
  isLoading = false,
  loading = false, // Alias for compatibility
  icon: Icon,
  children, 
  disabled,
  type = 'button',
  ...props 
}, ref) => {
  const isButtonLoading = isLoading || loading;
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded whitespace-nowrap shrink-0 font-semibold transition-colors duration-150 active:translate-y-px disabled:opacity-50 disabled:pointer-events-none border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0176D3] focus-visible:ring-offset-1 text-[13px]',
        variants[variant],
        sizes[size],
        className
      )}
      ref={ref}
      disabled={isButtonLoading || disabled}
      {...props}
    >
      {isButtonLoading ? (
        <Loader2 className="animate-spin" size={14} />
      ) : Icon ? (
        <Icon size={size === 'sm' ? 14 : size === 'lg' ? 18 : 14} />
      ) : null}
      <span className="leading-none inline-flex items-center gap-2">{children}</span>
    </button>
  );
});

Button.displayName = 'Button';

export default Button;

export { Button };
